import { getBearerToken, getSessionId } from '@entities/authentication'
import {
  convertRubyRequestSchema,
  convertRubyResponseSchema,
  copyPodoClassRoomRequestSchema,
  copyPodoClassRoomResponseSchema,
  createPodoChatRequestSchema,
  createPodoClassRequestSchema,
  createPodoPrestudyRoomRequestSchema,
  createPodoPrestudyRoomResponseSchema,
  createPodoPrestudyRoomSuccessResponseSchema,
  generateDiagnosisReportRequestSchema,
  generateDiagnosisReportResponseSchema,
  getAiCharacterDetailInfoRequestSchema,
  getAiCharacterDetailInfoResponseSchema,
  getBookingLectureInfoRequestSchema,
  getBookingLectureInfoResponseSchema,
  getActiveUnlimitedReservationConflictRequestSchema,
  getActiveUnlimitedReservationConflictResponseSchema,
  getCompletedLectureRequestSchema,
  getCompletedLectureResponseSchema,
  getHoldingListRequestSchema,
  getHoldingListResponseSchema,
  getLectureAndPrestudyRequestSchema,
  getLectureAndPrestudyResponseSchema,
  getLectureEnterInfoRequestSchema,
  getLectureEnterInfoResponseSchema,
  prestudyNextClassSchema,
  getLectureListRequestSchema,
  getLectureListResponseDataSchema,
  getLectureListResponseSchema,
  getLectureReplayVideoRequestSchema,
  getLectureReplayVideoResponseSchema,
  getLectureRoomInfoRequestSchema,
  getLectureRoomInfoResponseSchema,
  requestFinishPodoClassRequestSchema,
  requestFinishPodoClassResponseSchema,
  getLessonDiagnosisReportRequestSchema,
  getNextCourseRequestSchema,
  getNextCourseResponseSchema,
  getNextLectureInfoResponseSchema,
  getNextLectureListResponseSchema,
  getPenaltyListRequestSchema,
  getPenaltyListResponseSchema,
  getPodoPrestudyRoomMaterialsRequestSchema,
  getPodoPrestudyRoomMaterialsResponseSchema,
  getPodoPrestudyRoomRequestSchema,
  getPodoPrestudyRoomResponseSchema,
  getPronunciationListResponseSchema,
  getQuestionsRequestSchema,
  getQuestionsResponseSchema,
  getReplayEnterInfoRequestSchema,
  getReplayEnterInfoResponseSchema,
  getTutorSchedulesForRegRequestSchema,
  getTutorSchedulesForRegResponseSchema,
  getUserBookingActionResponseSchema,
  // 예약 관련 스키마
  getWeekDatesResponseSchema,
  invoiceStatusSchema,
  lessonDiagnosisReportResponseSchema,
  preStudySkipCountResponseSchema,
  selectLevelRequestSchema,
  selectLevelResponseSchema,
  skipPreStudyRequestSchema,
  skipPreStudyResponseSchema,
  updatePreStudyTimeRequestSchema,
  updatePreStudyTimeSuccessResponseSchema,
} from '@entities/lesson'
import { getSubscribeMappList } from '@entities/subscribes'
import { podoFetcher, toLegacyPodoErrorResponse } from '@server/infrastructure/external-apis/podo/client'
import { ticketLangTypeSchema } from '@server/schemas'
import type { Locale } from '@shared/config/i18n/locales'
import { z } from 'zod'

import { getCreatedClassId } from '../lib/helpers/action'
import { isPrestudyRoomCreationUnavailableError } from '../lib/helpers/prestudy-error'

type NormalizedOrLegacyResult<T> = {
  resultCd: number | string
  message?: string | null
  resultMsg?: string | null
  data?: T
  result?: T
}

const hasOwn = <K extends PropertyKey>(value: object, key: K): value is object & Record<K, unknown> =>
  Object.prototype.hasOwnProperty.call(value, key)

const getLegacyResultMsg = ({
  message,
  resultMsg,
}: Pick<NormalizedOrLegacyResult<unknown>, 'message' | 'resultMsg'>): string | undefined => {
  if (typeof message === 'string' && message.length > 0) {
    return message
  }

  if (resultMsg !== null && resultMsg !== undefined) {
    return resultMsg
  }

  return message === '' ? '' : undefined
}

const toLegacyResultResponse = <T>(response: NormalizedOrLegacyResult<T>) => {
  const resultMsg = getLegacyResultMsg(response)

  return {
    resultCd: String(response.resultCd),
    ...(resultMsg !== undefined ? { resultMsg } : {}),
    result: (hasOwn(response, 'data') ? response.data : response.result) as T,
  }
}

const toExpectedNotFoundLegacyResponse = <T>(error: unknown, options?: { fallbackResultToMessage?: boolean }) => {
  const response = toLegacyPodoErrorResponse<T>(error, options)

  if (response?.resultCd === '404') {
    return response
  }

  throw error
}

export const preStudySkipCountAction = async (
  bearerToken?: string,
): Promise<z.infer<typeof preStudySkipCountResponseSchema>> => {
  const authorizationToken = bearerToken ?? (await getBearerToken())

  const response = await podoFetcher<z.infer<typeof preStudySkipCountResponseSchema>>({
    method: 'GET',
    url: '/api/v1/user/podo/preStudySkipCount',
    header: { Authorization: `Bearer ${authorizationToken}` },
  })

  return preStudySkipCountResponseSchema.parse(response)
}

export const skipPreStudyAction = async (
  request: z.infer<typeof skipPreStudyRequestSchema>,
  bearerToken?: string,
): Promise<z.infer<typeof skipPreStudyResponseSchema>> => {
  const parsedRequest = skipPreStudyRequestSchema.parse(request)
  const authorizationToken = bearerToken ?? (await getBearerToken())

  const queryParams = new URLSearchParams({
    classCourseId: parsedRequest.classCourseId.toString(),
  })

  const response = await podoFetcher({
    method: 'POST',
    url: `/api/v1/user/podo/skipPrestudy?${queryParams.toString()}`,
    header: {
      Authorization: `Bearer ${authorizationToken}`,
    },
  })

  return skipPreStudyResponseSchema.parse(response)
}

export const selectLevelAction = async (
  request: z.infer<typeof selectLevelRequestSchema>,
  token?: string,
): Promise<z.infer<typeof selectLevelResponseSchema>> => {
  const parsedRequestBody = selectLevelRequestSchema.parse(request)

  const authorizationToken = token ?? (await getBearerToken())

  const response = await podoFetcher<z.infer<typeof selectLevelResponseSchema>>({
    method: 'POST',
    url: '/api/v2/leveltest/selectLevel',
    body: {
      lang_type: parsedRequestBody.langType,
      trial_level: parsedRequestBody.level,
    },
    header: {
      Authorization: `Bearer ${authorizationToken}`,
    },
  })

  return selectLevelResponseSchema.parse(response)
}

export const getNextCourseAction = async (
  request: z.infer<typeof getNextCourseRequestSchema>,
  bearerToken?: string,
): Promise<z.infer<typeof getNextCourseResponseSchema>> => {
  const parsedRequest = getNextCourseRequestSchema.parse(request)
  const authorizationToken = bearerToken ?? (await getBearerToken())

  const queryParams = new URLSearchParams({
    langType: parsedRequest.langType.toString(),
    curriculumType: parsedRequest.curriculumType.toString(),
    lessonTime: parsedRequest.lessonTime.toString(),
  })
  if (parsedRequest.strictLangType) {
    queryParams.set('strictLangType', 'true')
  }

  const response = await podoFetcher({
    url: `/api/v2/lecture/getNextCourse?${queryParams.toString()}`,
    method: 'GET',
    header: {
      Authorization: `Bearer ${authorizationToken}`,
    },
  })

  return getNextCourseResponseSchema.parse(response)
}

interface CreatedPodoClassFields {
  class_id?: string | number | null
  classId?: string | number | null
  prestudy_class_id?: unknown
  prestudyClassId?: unknown
}

interface CreatedPodoClassResult {
  class_id: string
  prestudy_class_id?: string
}

const validPrestudyClassIdSchema = z
  .union([z.string().trim().min(1), z.number()])
  .transform(Number)
  .pipe(z.number().int().positive().finite())
  .transform(String)

const readCreatedPrestudyClassId = (result: Pick<CreatedPodoClassFields, 'prestudy_class_id' | 'prestudyClassId'>) => {
  for (const value of [result.prestudy_class_id, result.prestudyClassId]) {
    const parsed = validPrestudyClassIdSchema.safeParse(value)
    if (parsed.success) {
      return parsed.data
    }
  }
  return null
}

const normalizeCreatedPodoClass = (result: CreatedPodoClassFields): CreatedPodoClassResult => {
  const prestudyClassId = readCreatedPrestudyClassId(result)
  return {
    class_id: getCreatedClassId(result),
    ...(prestudyClassId ? { prestudy_class_id: prestudyClassId } : {}),
  }
}

export const createPodoClass = async (request: z.infer<typeof createPodoClassRequestSchema>, bearerToken?: string) => {
  const sessionId = await getSessionId()
  const parsedRequest = createPodoClassRequestSchema.parse(request)
  const authorizationToken = bearerToken ?? (await getBearerToken())
  const response = await podoFetcher<{
    resultCd: string
    result: CreatedPodoClassFields | string
  }>({
    method: 'POST',
    url: `/api/v2/lecture/podo/createPodoClass?${new URLSearchParams({ classCourseId: parsedRequest.classCourseId.toString(), randomUserKey: sessionId })}`,
    header: {
      Authorization: `Bearer ${authorizationToken}`,
    },
  })

  if (!['200', '201'].includes(String(response.resultCd))) {
    // 서버에서 에러 메시지가 문자열로 오는 경우 (예: 404, 403 등)
    if (typeof response.data === 'string') {
      throw new Error(response.data)
    }
    throw new Error(String(response.resultCd))
  }

  return normalizeCreatedPodoClass(response.data as CreatedPodoClassFields)
}

export const createPodoChat = async (request: z.infer<typeof createPodoChatRequestSchema>, bearerToken?: string) => {
  const sessionId = await getSessionId()
  const parsedRequest = createPodoChatRequestSchema.parse(request)
  const authorizationToken = bearerToken ?? (await getBearerToken())
  const response = await podoFetcher<{
    resultCd: string
    result: { class_id?: string; classId?: string } | string
  }>({
    method: 'POST',
    url: `/api/v2/lecture/podo/createPodoChat?${new URLSearchParams({ classCourseId: parsedRequest.classCourseId.toString(), randomUserKey: sessionId })}`,
    header: {
      Authorization: `Bearer ${authorizationToken}`,
    },
  })

  if (!['200', '201'].includes(String(response.resultCd))) {
    throw new Error(String(response.resultCd))
  }

  return {
    class_id: getCreatedClassId(response.data as { class_id?: string; classId?: string }),
  }
}

// 수업 접속 정보 조회
export const getLectureEnterInfo = async (
  request: z.infer<typeof getLectureEnterInfoRequestSchema>,
  bearerToken?: string,
  options?: { useV3?: boolean },
) => {
  const parsedRequest = getLectureEnterInfoRequestSchema.parse(request)
  const authorizationToken = bearerToken ?? (await getBearerToken())

  const queryParams = new URLSearchParams({
    classId: parsedRequest.classId.toString(),
  })

  const endpoint = options?.useV3 ? 'getLeLectureEnterInfoV3' : 'getLeLectureEnterInfo'

  try {
    const response = await podoFetcher<z.infer<typeof getLectureEnterInfoResponseSchema>>({
      url: `/api/v2/lecture/${endpoint}?${queryParams.toString()}`,
      method: 'GET',
      header: {
        Authorization: `Bearer ${authorizationToken}`,
      },
    })

    return getLectureEnterInfoResponseSchema.parse(toLegacyResultResponse(response))
  } catch (error) {
    return getLectureEnterInfoResponseSchema.parse(
      toExpectedNotFoundLegacyResponse<string>(error, { fallbackResultToMessage: true }),
    )
  }
}

/** 반복 조회는 교실 입장 API의 방 생성·입장 알림을 실행하지 않는다. */
export const getPrestudyNextClass = async (classId: number, bearerToken?: string) => {
  const authorizationToken = bearerToken ?? (await getBearerToken())
  const validatedClassId = z.number().int().positive().parse(classId)
  try {
    const response = await podoFetcher({
      url: `/api/v2/lecture/prestudy/${validatedClassId}/next-class`,
      method: 'GET',
      header: { Authorization: `Bearer ${authorizationToken}` },
    })
    return z
      .object({ resultCd: z.literal('200'), result: prestudyNextClassSchema.nullable() })
      .parse(toLegacyResultResponse(response)).result
  } catch (error) {
    // 배포 순서가 섞여 새 GET을 모르는 서버의 404만 구분한다. 다른 오류는 예약 없음이 아니다.
    toExpectedNotFoundLegacyResponse(error)
    return undefined
  }
}

export const updatePreStudyTime = async (
  request: z.infer<typeof updatePreStudyTimeRequestSchema>,
  bearerToken?: string,
) => {
  const parsedRequest = updatePreStudyTimeRequestSchema.parse(request)
  const authorizationToken = bearerToken ?? (await getBearerToken())

  const queryParams = new URLSearchParams({
    classId: parsedRequest.classId.toString(),
    randomUserKey: parsedRequest.randomUserKey,
    studentId: parsedRequest.studentId.toString(),
  })

  const response = await podoFetcher<z.infer<typeof updatePreStudyTimeSuccessResponseSchema>>(
    {
      url: `/api/v2/lecture/podo/updatePreStudyTime?${queryParams.toString()}`,
      method: 'POST',
      header: {
        Authorization: `Bearer ${authorizationToken}`,
      },
    },
    { responseType: 'raw' },
  )

  return updatePreStudyTimeSuccessResponseSchema.parse(response)
}

export const getReplayEnterInfo = async (
  request: z.infer<typeof getReplayEnterInfoRequestSchema>,
  bearerToken?: string,
) => {
  const parsedRequest = getReplayEnterInfoRequestSchema.parse(request)
  const authorizationToken = bearerToken ?? (await getBearerToken())

  const queryParams = new URLSearchParams({
    classId: parsedRequest.classId.toString(),
  })

  try {
    const response = await podoFetcher<z.infer<typeof getReplayEnterInfoResponseSchema>>({
      url: `/api/v1/lecture/getLeLectureReplayInfo?${queryParams.toString()}`,
      method: 'GET',
      header: {
        Authorization: `Bearer ${authorizationToken}`,
      },
    })

    return getReplayEnterInfoResponseSchema.parse(toLegacyResultResponse(response))
  } catch (error) {
    return getReplayEnterInfoResponseSchema.parse(toExpectedNotFoundLegacyResponse<null>(error))
  }
}

/**
 * 다시보기 mp4 생성 요청. 멱등이다 — 이미 진행 중이거나 완료돼 있으면 새로 만들지 않고
 * 그 상태가 돌아온다. 실제 생성은 레몬보드가 비동기로 하며 getLectureReplayVideo 로 폴링한다.
 */
export const requestLectureReplayVideo = async (
  request: z.infer<typeof getLectureReplayVideoRequestSchema>,
  bearerToken?: string,
) => {
  const parsedRequest = getLectureReplayVideoRequestSchema.parse(request)
  const authorizationToken = bearerToken ?? (await getBearerToken())

  const queryParams = new URLSearchParams({
    classId: parsedRequest.classId.toString(),
  })

  try {
    const response = await podoFetcher<z.infer<typeof getLectureReplayVideoResponseSchema>>({
      url: `/api/v1/lecture/replayVideo?${queryParams.toString()}`,
      method: 'POST',
      header: {
        Authorization: `Bearer ${authorizationToken}`,
      },
    })

    return getLectureReplayVideoResponseSchema.parse(toLegacyResultResponse(response))
  } catch (error) {
    return getLectureReplayVideoResponseSchema.parse(toExpectedNotFoundLegacyResponse<null>(error))
  }
}

export const getLectureReplayVideo = async (
  request: z.infer<typeof getLectureReplayVideoRequestSchema>,
  bearerToken?: string,
) => {
  const parsedRequest = getLectureReplayVideoRequestSchema.parse(request)
  const authorizationToken = bearerToken ?? (await getBearerToken())

  const queryParams = new URLSearchParams({
    classId: parsedRequest.classId.toString(),
  })

  try {
    const response = await podoFetcher<z.infer<typeof getLectureReplayVideoResponseSchema>>({
      url: `/api/v1/lecture/replayVideo?${queryParams.toString()}`,
      method: 'GET',
      header: {
        Authorization: `Bearer ${authorizationToken}`,
      },
    })

    return getLectureReplayVideoResponseSchema.parse(toLegacyResultResponse(response))
  } catch (error) {
    return getLectureReplayVideoResponseSchema.parse(toExpectedNotFoundLegacyResponse<null>(error))
  }
}

export const getLectureRoomInfo = async (
  request: z.infer<typeof getLectureRoomInfoRequestSchema>,
  bearerToken?: string,
) => {
  const parsedRequest = getLectureRoomInfoRequestSchema.parse(request)
  const authorizationToken = bearerToken ?? (await getBearerToken())

  const queryParams = new URLSearchParams({
    classId: parsedRequest.classId.toString(),
  })

  try {
    const response = await podoFetcher<z.infer<typeof getLectureRoomInfoResponseSchema>>({
      url: `/api/v1/lecture/getLeLectureRoomInfo?${queryParams.toString()}`,
      method: 'GET',
      header: {
        Authorization: `Bearer ${authorizationToken}`,
      },
    })

    return getLectureRoomInfoResponseSchema.parse(toLegacyResultResponse(response))
  } catch (error) {
    return getLectureRoomInfoResponseSchema.parse(
      toExpectedNotFoundLegacyResponse<string>(error, { fallbackResultToMessage: true }),
    )
  }
}

export const requestFinishPodoClass = async (
  request: z.infer<typeof requestFinishPodoClassRequestSchema>,
  bearerToken?: string,
) => {
  const parsedRequest = requestFinishPodoClassRequestSchema.parse(request)
  const authorizationToken = bearerToken ?? (await getBearerToken())

  const queryParams = new URLSearchParams({
    classId: parsedRequest.classId.toString(),
    randomUserKey: parsedRequest.randomUserKey,
    studentId: parsedRequest.studentId.toString(),
  })

  const response = await podoFetcher<z.infer<typeof requestFinishPodoClassResponseSchema>>(
    {
      url: `/api/v2/lecture/podo/finishPreStudyTime?${queryParams.toString()}`,
      method: 'POST',
      header: {
        Authorization: `Bearer ${authorizationToken}`,
      },
    },
    { responseType: 'raw' },
  )

  return requestFinishPodoClassResponseSchema.parse(response)
}

export const generateDiagnosisReport = async (
  request: z.infer<typeof generateDiagnosisReportRequestSchema>,
  bearerToken?: string,
): Promise<z.infer<typeof generateDiagnosisReportResponseSchema>> => {
  const parsedRequest = generateDiagnosisReportRequestSchema.parse(request)
  const authorizationToken = bearerToken ?? (await getBearerToken())

  const queryParams = new URLSearchParams({
    classId: parsedRequest.classId.toString(),
  })

  const response = await podoFetcher({
    url: `/api/v1/diagnosis/generateDiagnosisReport?${queryParams.toString()}`,
    method: 'GET',
    header: {
      Authorization: `Bearer ${authorizationToken}`,
    },
  })
  return generateDiagnosisReportResponseSchema.parse(response)
}

export const getDiagnosisReport = async (
  request: z.infer<typeof getLessonDiagnosisReportRequestSchema>,
  bearerToken?: string,
): Promise<z.infer<typeof lessonDiagnosisReportResponseSchema>> => {
  const parsedRequest = getLessonDiagnosisReportRequestSchema.parse(request)
  const authorizationToken = bearerToken ?? (await getBearerToken())

  const queryParams = new URLSearchParams({
    classId: parsedRequest.classId.toString(),
  })

  const response = await podoFetcher({
    url: `/api/v1/diagnosis/getDiagnosisReport?${queryParams.toString()}`,
    method: 'GET',
    header: {
      Authorization: `Bearer ${authorizationToken}`,
    },
  })

  return lessonDiagnosisReportResponseSchema.parse(response)
}

export const getQuestions = async (
  request: z.infer<typeof getQuestionsRequestSchema>,
  bearerToken?: string,
): Promise<z.infer<typeof getQuestionsResponseSchema>> => {
  const parsedRequest = getQuestionsRequestSchema.parse(request)
  const authorizationToken = bearerToken ?? (await getBearerToken())

  const queryParams = new URLSearchParams({
    classId: parsedRequest.classId.toString(),
    feedbackIds: parsedRequest.feedbackIds.join(','),
  })

  const response = await podoFetcher({
    url: `/api/v1/diagnosis/getQuestions?${queryParams.toString()}`,
    method: 'GET',
    header: {
      Authorization: `Bearer ${authorizationToken}`,
    },
  })

  return getQuestionsResponseSchema.parse(response)
}

export const getReservedLessons = async (
  bearerToken?: string,
  locale: Locale = 'ko',
  pagination: { page?: number; limit?: number } = {},
): Promise<z.infer<typeof getLectureListResponseDataSchema>> => {
  const authorizationToken = bearerToken ?? (await getBearerToken())
  const { page = 1, limit = 10 } = pagination

  const response = await podoFetcher<z.infer<typeof getLectureListResponseSchema>>({
    url: `/api/v2/lecture/podo/getLectureList?page=${page}&limit=${limit}&order=asc&invoice_status=${invoiceStatusSchema.Enum.RESERVED}`,
    method: 'GET',
    header: {
      Authorization: `Bearer ${authorizationToken}`,
      'Accept-Language': locale,
    },
  })

  return getLectureListResponseDataSchema.parse(response.data)
}

export const getCompletedLessons = async (
  request: z.infer<typeof getLectureListRequestSchema>,
  bearerToken?: string,
  locale: Locale = 'ko',
): Promise<z.infer<typeof getLectureListResponseDataSchema>> => {
  const parsedRequest = getLectureListRequestSchema.parse(request)
  const authorizationToken = bearerToken ?? (await getBearerToken())

  const queryParams = new URLSearchParams({
    page: parsedRequest.page.toString(),
    limit: parsedRequest.limit.toString(),
    order: parsedRequest.order.toString(),
    invoice_status: parsedRequest.invoice_status.join(','),
  })

  const response = await podoFetcher<z.infer<typeof getLectureListResponseSchema>>({
    url: `/api/v2/lecture/podo/getLectureList?${queryParams.toString()}`,
    method: 'GET',
    header: {
      Authorization: `Bearer ${authorizationToken}`,
      'Accept-Language': locale,
    },
  })

  return getLectureListResponseDataSchema.parse(response.data)
}

export const convertRuby = async (request: z.infer<typeof convertRubyRequestSchema>, bearerToken?: string) => {
  const parsedRequest = convertRubyRequestSchema.parse(request)
  const authorizationToken = bearerToken

  const response = await fetch('https://lemonboard-api.re-speak.com/action/kanji', {
    method: 'POST',
    body: JSON.stringify(parsedRequest),
    headers: authorizationToken ? { Authorization: `Bearer ${authorizationToken}` } : {},
  })

  const json = await response.json()

  return convertRubyResponseSchema.parse(json)
}

export const getNextLectureInfo = async (
  bearerToken?: string,
): Promise<z.infer<typeof getNextLectureInfoResponseSchema>> => {
  const authorizationToken = bearerToken ?? (await getBearerToken())

  const response = await podoFetcher<z.infer<typeof getNextLectureInfoResponseSchema>>({
    method: 'GET',
    url: '/api/v1/lecture/podo/getNextLectureInfo',
    header: {
      Authorization: `Bearer ${authorizationToken}`,
    },
  })

  return getNextLectureInfoResponseSchema.parse(toLegacyResultResponse(response))
}

export const getPronunciationList = async (bearerToken?: string) => {
  const authorizationToken = bearerToken ?? (await getBearerToken())

  const response = await podoFetcher<z.infer<typeof getPronunciationListResponseSchema>>({
    method: 'GET',
    url: '/api/v1/lecture/podo/getPronunciationList',
    header: {
      Authorization: `Bearer ${authorizationToken}`,
    },
  })

  return getPronunciationListResponseSchema.parse(response.data)
}

export const getNextLectureList = async (
  bearerToken?: string,
): Promise<z.infer<typeof getNextLectureListResponseSchema>> => {
  const authorizationToken = bearerToken ?? (await getBearerToken())

  const response = await podoFetcher<z.infer<typeof getNextLectureListResponseSchema>>({
    method: 'GET',
    url: '/api/v1/lecture/podo/getNextLectureList',
    header: {
      Authorization: `Bearer ${authorizationToken}`,
    },
  })

  if (response.resultCd >= 400) {
    console.error('Failed to fetch user bookings:', response)
    throw new Error(`Failed to fetch user bookings: ${response.message || 'Unknown error'}`)
  }

  return response
}

/**
 * Get user's booked lessons with fallback to recommended course when empty
 * Consolidated from multiple implementations to single source of truth
 */
export const getUserBookingsAction = async (
  bearerToken?: string,
): Promise<z.infer<typeof getUserBookingActionResponseSchema>> => {
  const authorizationToken = bearerToken ?? (await getBearerToken())

  try {
    // Step 1: Try to get scheduled lessons first
    const getNextLectureListResponse = await getNextLectureList()

    // Step 2: If user has scheduled lessons, return them
    if (getNextLectureListResponse.data.length > 0) {
      return getNextLectureListResponse.data
    }

    const subscribeMappList = await getSubscribeMappList(authorizationToken)

    if (subscribeMappList.result.length === 0) {
      return []
    }

    const parsedLangType = ticketLangTypeSchema.safeParse(subscribeMappList.result[0].lang_type)
    if (!parsedLangType.success) {
      return []
    }

    const recommendedCourse = await getNextCourseAction(
      {
        langType: parsedLangType.data, // Default to English, could be parameterized later
        curriculumType: subscribeMappList.result[0].curriculum_type, // Default to Business, could be parameterized later
        lessonTime: subscribeMappList.result[0].lesson_time, // Default to 25 minutes, could be parameterized later
      },
      authorizationToken,
    )

    // Step 4: Transform recommended course into lesson format
    if (recommendedCourse.data) {
      const recommendedLesson = {
        id: `recommended_${recommendedCourse.data.course_id}`,
        classId: recommendedCourse.data.course_id,
        title: `Recommended: ${recommendedCourse.data.level_name} - Week ${recommendedCourse.data.week}`,
        instructor: 'Available Instructors',
        scheduledAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
        duration: subscribeMappList.result[0].lesson_time,
        level: recommendedCourse.data.level_name,
        status: 'recommended' as const,
        canCancel: false,
      }

      return [recommendedLesson]
    }

    // Step 5: If no recommended course available either, return empty array
    return []
  } catch (error) {
    console.error('Failed to get user bookings:', error)
    throw new Error('Failed to load your bookings')
  }
}

/**
 * Create Podo class for lesson booking
 * Consolidated from multiple implementations to single source of truth
 */
export const CREATE_PODO_CLASS_FAILED_CODE = 'CREATE_PODO_CLASS_FAILED'
export const CREATE_PRESTUDY_ROOM_FAILED_CODE = 'CREATE_PRESTUDY_ROOM_FAILED'
export const PRESTUDY_ROOM_NOT_FOUND_CODE = 'PRESTUDY_ROOM_NOT_FOUND'
export const PRESTUDY_ROOM_MATERIALS_NOT_FOUND_CODE = 'PRESTUDY_ROOM_MATERIALS_NOT_FOUND'

export const createPodoClassAction = async (
  request: { classCourseId: number; sessionId: string },
  bearerToken?: string,
): Promise<CreatedPodoClassResult> => {
  const authorizationToken = bearerToken ?? (await getBearerToken())

  const response = await podoFetcher<{
    resultCd: string
    result: CreatedPodoClassFields | string
  }>({
    method: 'POST',
    url: `/api/v2/lecture/podo/createPodoClass?classCourseId=${request.classCourseId}&randomUserKey=${request.sessionId}`,
    header: {
      Authorization: `Bearer ${authorizationToken}`,
    },
  })

  if (!['200', '201'].includes(String(response.resultCd))) {
    const resultMessage = typeof response.data === 'string' ? response.data : undefined
    throw new Error(resultMessage || CREATE_PODO_CLASS_FAILED_CODE)
  }

  return normalizeCreatedPodoClass(response.data as CreatedPodoClassFields)
}

/**
 * Create Lemonboard prestudy room. Used only for prestudy entry.
 */
type CreatePodoPrestudyRoomSuccessResult = z.infer<typeof createPodoPrestudyRoomSuccessResponseSchema>['result']

export type CreatePodoPrestudyRoomResult =
  | {
      entryTargetType: string
      classCourseId: string
      prestudyRoomId: string
      prestudyRoomUrl: string
    }
  | {
      entryTargetType: string
      classId: string
    }

const normalizeEntryTargetHint = (result: CreatePodoPrestudyRoomSuccessResult) => {
  return [result.entryTargetType, result.idType]
    .filter((value): value is string => typeof value === 'string')
    .join('|')
    .toUpperCase()
}

const getLegacyPrestudyClassId = (result: CreatePodoPrestudyRoomSuccessResult) => {
  const explicitClassId = readCreatedPrestudyClassId(result) ?? result.classId ?? result.class_id

  if (explicitClassId) {
    return explicitClassId
  }

  if (normalizeEntryTargetHint(result).includes('CLASS')) {
    return result.id ?? null
  }

  return null
}

const getLemonboardRoomId = (result: CreatePodoPrestudyRoomSuccessResult) => {
  if (result.prestudyRoomId) {
    return result.prestudyRoomId
  }

  const entryTargetHint = normalizeEntryTargetHint(result)
  if (entryTargetHint.includes('ROOM') || entryTargetHint.includes('LEMONBOARD')) {
    return result.id ?? null
  }

  return null
}

const createGenericPrestudyRoomError = (cause: unknown) => {
  const error = new Error(CREATE_PRESTUDY_ROOM_FAILED_CODE)
  ;(error as Error & { cause?: unknown }).cause = cause
  return error
}

export const createPodoPrestudyRoomAction = async (
  request: z.infer<typeof createPodoPrestudyRoomRequestSchema>,
  bearerToken?: string,
): Promise<CreatePodoPrestudyRoomResult> => {
  const parsedRequest = createPodoPrestudyRoomRequestSchema.parse(request)
  const authorizationToken = bearerToken ?? (await getBearerToken())
  const queryParams = new URLSearchParams({
    classCourseId: parsedRequest.classCourseId.toString(),
    randomUserKey: parsedRequest.sessionId,
  })

  let parsedResponse: z.infer<typeof createPodoPrestudyRoomResponseSchema>

  try {
    const response = await podoFetcher<z.infer<typeof createPodoPrestudyRoomResponseSchema>>({
      method: 'POST',
      url: `/api/v3/lecture/podo/createPrestudyRoom?${queryParams.toString()}`,
      header: {
        Authorization: `Bearer ${authorizationToken}`,
      },
    })

    parsedResponse = createPodoPrestudyRoomResponseSchema.parse(toLegacyResultResponse(response))
  } catch (error) {
    if (isPrestudyRoomCreationUnavailableError(error)) {
      throw error
    }

    throw createGenericPrestudyRoomError(error)
  }

  if (parsedResponse.resultCd === '409') {
    throw new Error(parsedResponse.resultMsg ?? CREATE_PRESTUDY_ROOM_FAILED_CODE)
  }

  const legacyClassId = getLegacyPrestudyClassId(parsedResponse.result)
  const prestudyRoomId = getLemonboardRoomId(parsedResponse.result)

  if (legacyClassId && (!prestudyRoomId || normalizeEntryTargetHint(parsedResponse.result).includes('CLASS'))) {
    return {
      entryTargetType: parsedResponse.result.entryTargetType ?? 'LEGACY_CLASSROOM',
      classId: legacyClassId,
    }
  }

  if (prestudyRoomId) {
    return {
      classCourseId: parsedResponse.result.classCourseId ?? parsedRequest.classCourseId.toString(),
      entryTargetType: parsedResponse.result.entryTargetType ?? 'LEMONBOARD_ROOM',
      prestudyRoomId,
      prestudyRoomUrl: parsedResponse.result.prestudyRoomUrl ?? '',
    }
  }

  throw new Error(CREATE_PRESTUDY_ROOM_FAILED_CODE)
}

export const getPodoPrestudyRoomAction = async (
  request: z.infer<typeof getPodoPrestudyRoomRequestSchema>,
  bearerToken?: string,
): Promise<z.infer<typeof getPodoPrestudyRoomResponseSchema>> => {
  const parsedRequest = getPodoPrestudyRoomRequestSchema.parse(request)
  const authorizationToken = bearerToken ?? (await getBearerToken())

  try {
    const response = await podoFetcher<z.infer<typeof getPodoPrestudyRoomResponseSchema>>({
      method: 'GET',
      url: `/api/v3/lecture/podo/prestudy-room/${encodeURIComponent(parsedRequest.roomId)}`,
      header: {
        Authorization: `Bearer ${authorizationToken}`,
      },
    })

    return getPodoPrestudyRoomResponseSchema.parse(toLegacyResultResponse(response))
  } catch (error) {
    const cause = error instanceof Error ? (error as { cause?: Record<string, unknown> }).cause : undefined

    if (String(cause?.resultCd ?? '') === '404') {
      const resultMsg =
        typeof cause?.resultMsg === 'string'
          ? cause.resultMsg
          : typeof cause?.message === 'string'
            ? cause.message
            : PRESTUDY_ROOM_NOT_FOUND_CODE

      return getPodoPrestudyRoomResponseSchema.parse({
        resultCd: '404',
        resultMsg,
      })
    }

    throw error
  }
}

export const getPodoPrestudyRoomMaterialsAction = async (
  request: z.infer<typeof getPodoPrestudyRoomMaterialsRequestSchema>,
  bearerToken?: string,
): Promise<z.infer<typeof getPodoPrestudyRoomMaterialsResponseSchema>> => {
  const parsedRequest = getPodoPrestudyRoomMaterialsRequestSchema.parse(request)
  const authorizationToken = bearerToken ?? (await getBearerToken())
  const queryParams = new URLSearchParams({
    classCourseId: parsedRequest.classCourseId.toString(),
  })

  try {
    const response = await podoFetcher<z.infer<typeof getPodoPrestudyRoomMaterialsResponseSchema>>({
      method: 'GET',
      url: `/api/v3/lecture/podo/prestudy-room/materials?${queryParams.toString()}`,
      header: {
        Authorization: `Bearer ${authorizationToken}`,
      },
    })

    return getPodoPrestudyRoomMaterialsResponseSchema.parse(toLegacyResultResponse(response))
  } catch (error) {
    const cause = error instanceof Error ? (error as { cause?: Record<string, unknown> }).cause : undefined

    if (String(cause?.resultCd ?? '') === '404') {
      const resultMsg =
        typeof cause?.resultMsg === 'string'
          ? cause.resultMsg
          : typeof cause?.message === 'string'
            ? cause.message
            : PRESTUDY_ROOM_MATERIALS_NOT_FOUND_CODE

      return getPodoPrestudyRoomMaterialsResponseSchema.parse({
        resultCd: '404',
        resultMsg,
      })
    }

    throw error
  }
}

/**
 * Cancel a booked lesson
 * Consolidated from multiple implementations to single source of truth
 */
export const cancelBookingAction = async (
  request: { classId: number },
  bearerToken?: string,
): Promise<{ penaltyWaived: boolean }> => {
  const authorizationToken = bearerToken ?? (await getBearerToken())

  const response = await podoFetcher<{
    resultCd: number
    resultCdName: string
    message: string | null
    data: { message: string; penalty_waived: boolean } | null
  }>({
    method: 'POST',
    url: '/api/v3/schedule/cancel',
    body: {
      class_id: request.classId,
      push_yn: 'Y',
      penalty_yn: 'Y',
    },
    header: {
      Authorization: `Bearer ${authorizationToken}`,
    },
  })

  if (response.resultCd !== 200) {
    throw new Error(`Failed to cancel booking: ${response.message || 'Unknown error'}`)
  }

  return { penaltyWaived: response.data?.penalty_waived ?? false }
}

export const getCompletedLecture = async (
  request: z.infer<typeof getCompletedLectureRequestSchema>,
  bearerToken?: string,
): Promise<z.infer<typeof getCompletedLectureResponseSchema>> => {
  const authorizationToken = bearerToken ?? (await getBearerToken())
  const parsedRequest = getCompletedLectureRequestSchema.parse(request)

  const searchParams = new URLSearchParams({
    class_id: parsedRequest.classId.toString(),
  })

  const response = await podoFetcher({
    url: `/api/v2/lecture/podo/getCompletedLecture?${searchParams.toString()}`,
    method: 'GET',
    header: {
      Authorization: `Bearer ${authorizationToken}`,
    },
  })

  return getCompletedLectureResponseSchema.parse(response)
}

export const copyPodoClassRoom = async (
  request: z.infer<typeof copyPodoClassRoomRequestSchema>,
  bearerToken?: string,
) => {
  const authorizationToken = bearerToken ?? (await getBearerToken())
  const parsedRequest = copyPodoClassRoomRequestSchema.parse(request)

  const searchParams = new URLSearchParams({
    class_id: parsedRequest.classId.toString(),
  })

  const response = await podoFetcher({
    url: `/api/v2/lecture/podo/copyPodoClassRoom?${searchParams.toString()}`,
    method: 'POST',
    header: {
      Authorization: `Bearer ${authorizationToken}`,
    },
  })

  return copyPodoClassRoomResponseSchema.parse(response)
}

export const getAiCharacterDetailInfo = async (
  request: z.infer<typeof getAiCharacterDetailInfoRequestSchema>,
  bearerToken?: string,
  locale = 'en',
) => {
  const authorizationToken = bearerToken ?? (await getBearerToken())
  const parsedRequest = getAiCharacterDetailInfoRequestSchema.parse(request)

  const searchParams = new URLSearchParams({
    classCourseId: parsedRequest.classCourseId.toString(),
  })

  const response = await podoFetcher({
    url: `/api/v2/lecture/ai/character/detail?${searchParams.toString()}`,
    method: 'GET',
    header: {
      Authorization: `Bearer ${authorizationToken}`,
      'Accept-Language': locale,
    },
  })

  return getAiCharacterDetailInfoResponseSchema.parse(response)
}

export const getLectureAndPrestudyAction = async (
  request: z.infer<typeof getLectureAndPrestudyRequestSchema>,
  bearerToken?: string,
): Promise<z.infer<typeof getLectureAndPrestudyResponseSchema>> => {
  const parsedRequest = getLectureAndPrestudyRequestSchema.parse(request)
  const authorizationToken = bearerToken ?? (await getBearerToken())

  const queryParams = new URLSearchParams({
    classCourseId: parsedRequest.classCourseId.toString(),
  })

  const response = await podoFetcher({
    url: `/api/v2/lecture/getLectureAndPrestudy?${queryParams.toString()}`,
    method: 'GET',
    header: {
      Authorization: `Bearer ${authorizationToken}`,
    },
  })

  return getLectureAndPrestudyResponseSchema.parse(response)
}

// ============================================
// 예약 관련 API 함수들 (Booking Schedules)
// ============================================

/**
 * 예약 가능한 날짜 목록 조회 (2주간)
 */
export const getWeekDates = async (bearerToken?: string): Promise<z.infer<typeof getWeekDatesResponseSchema>> => {
  const authorizationToken = bearerToken ?? (await getBearerToken())

  const response = await podoFetcher<z.infer<typeof getWeekDatesResponseSchema>>({
    url: '/api/v1/common/getWeekDates',
    method: 'GET',
    header: {
      Authorization: `Bearer ${authorizationToken}`,
    },
  })

  return getWeekDatesResponseSchema.parse(toLegacyResultResponse(response))
}

/**
 * 튜터 스케줄 조회 (예약 가능 시간 슬롯)
 */
export const getTutorSchedulesForReg = async (
  request: z.infer<typeof getTutorSchedulesForRegRequestSchema>,
  bearerToken?: string,
): Promise<z.infer<typeof getTutorSchedulesForRegResponseSchema>> => {
  const parsedRequest = getTutorSchedulesForRegRequestSchema.parse(request)
  const authorizationToken = bearerToken ?? (await getBearerToken())

  const queryParams = new URLSearchParams({
    classId: parsedRequest.classId.toString(),
    startDateTime: parsedRequest.startDateTime,
    endDateTime: parsedRequest.endDateTime,
  })

  const response = await podoFetcher({
    url: `/api/v3/schedule/getTutorSchedulesForReg?${queryParams.toString()}`,
    method: 'GET',
    header: {
      Authorization: `Bearer ${authorizationToken}`,
    },
  })

  return getTutorSchedulesForRegResponseSchema.parse(response)
}

/**
 * 예약용 레슨 정보 조회
 */
export const getBookingLectureInfo = async (
  request: z.infer<typeof getBookingLectureInfoRequestSchema>,
  bearerToken?: string,
): Promise<z.infer<typeof getBookingLectureInfoResponseSchema>> => {
  const parsedRequest = getBookingLectureInfoRequestSchema.parse(request)
  const authorizationToken = bearerToken ?? (await getBearerToken())

  const queryParams = new URLSearchParams({
    classId: parsedRequest.classId.toString(),
  })

  const response = await podoFetcher({
    url: `/api/v3/schedule/getLectureInfo?${queryParams.toString()}`,
    method: 'GET',
    header: {
      Authorization: `Bearer ${authorizationToken}`,
    },
  })

  return getBookingLectureInfoResponseSchema.parse(response)
}

export const getActiveUnlimitedReservationConflict = async (
  request: z.infer<typeof getActiveUnlimitedReservationConflictRequestSchema>,
  bearerToken?: string,
): Promise<z.infer<typeof getActiveUnlimitedReservationConflictResponseSchema>> => {
  const parsedRequest = getActiveUnlimitedReservationConflictRequestSchema.parse(request)
  const authorizationToken = bearerToken ?? (await getBearerToken())

  const queryParams = new URLSearchParams({
    classCourseId: parsedRequest.classCourseId.toString(),
  })

  const response = await podoFetcher({
    url: `/api/v3/schedule/active-unlimited-reservation-conflict?${queryParams.toString()}`,
    method: 'GET',
    header: {
      Authorization: `Bearer ${authorizationToken}`,
    },
  })

  return getActiveUnlimitedReservationConflictResponseSchema.parse(response)
}

/**
 * 페널티 목록 조회
 */
export const getPenaltyList = async (
  request: z.infer<typeof getPenaltyListRequestSchema>,
  bearerToken?: string,
): Promise<z.infer<typeof getPenaltyListResponseSchema>> => {
  const parsedRequest = getPenaltyListRequestSchema.parse(request)
  const authorizationToken = bearerToken ?? (await getBearerToken())

  const queryParams = new URLSearchParams({
    classId: parsedRequest.classId.toString(),
  })

  const response = await podoFetcher<z.infer<typeof getPenaltyListResponseSchema>>({
    url: `/api/v1/user/podo/penaltyList?${queryParams.toString()}`,
    method: 'GET',
    header: {
      Authorization: `Bearer ${authorizationToken}`,
    },
  })

  return getPenaltyListResponseSchema.parse(toLegacyResultResponse(response))
}

/**
 * 홀딩 목록 조회
 */
export const getHoldingList = async (
  request: z.infer<typeof getHoldingListRequestSchema>,
  bearerToken?: string,
): Promise<z.infer<typeof getHoldingListResponseSchema>> => {
  const parsedRequest = getHoldingListRequestSchema.parse(request)
  const authorizationToken = bearerToken ?? (await getBearerToken())

  const queryParams = new URLSearchParams({
    classId: parsedRequest.classId.toString(),
  })

  const response = await podoFetcher<z.infer<typeof getHoldingListResponseSchema>>({
    url: `/api/v1/user/podo/holdingList?${queryParams.toString()}`,
    method: 'GET',
    header: {
      Authorization: `Bearer ${authorizationToken}`,
    },
  })

  return getHoldingListResponseSchema.parse(toLegacyResultResponse(response))
}

export const ensureRegularClassAction = async (
  request: { classCourseId: number },
  bearerToken?: string,
): Promise<{ classId: number }> => {
  const authorizationToken = bearerToken ?? (await getBearerToken())
  const queryParams = new URLSearchParams({
    classCourseId: request.classCourseId.toString(),
  })

  const response = await podoFetcher<{ classId: number }>({
    method: 'POST',
    url: `/api/v3/lecture/podo/ensure-regular-class?${queryParams.toString()}`,
    header: {
      Authorization: `Bearer ${authorizationToken}`,
    },
  })

  if (!['200', '201'].includes(String(response.resultCd))) {
    throw new Error('수업 예약에 실패했습니다.')
  }

  // podoFetcher normalizes backend's "result" field → response.data
  const classId = response.data?.classId
  if (!classId) {
    throw new Error('수업 예약에 실패했습니다.')
  }

  return { classId }
}
